<template>
  <div class="content mt-invoices-page">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("modern_trade.invoice.title") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
        <el-tabs v-model="activeTab" class="px-4 pt-3">
          <!-- ═══ Tab 1: Invoices ═══ -->
          <el-tab-pane :label="$t('modern_trade.invoice.tab_invoices')" name="invoices">
            <div class="row align-items-end mb-3">
              <div class="col-md-3">
                <label class="form-control-label">{{ $t("modern_trade.invoice.company") }}</label>
                <el-select
                  v-model="filters.company_id"
                  :placeholder="$t('all') || 'All'"
                  clearable
                  @change="loadInvoices"
                  class="w-100"
                >
                  <el-option
                    v-for="c in companies"
                    :key="c.id"
                    :label="c.name"
                    :value="c.id"
                  ></el-option>
                </el-select>
              </div>
              <div class="col-md-2">
                <label class="form-control-label">{{ $t("modern_trade.invoice.status") }}</label>
                <el-select
                  v-model="filters.status"
                  :placeholder="$t('all') || 'All'"
                  clearable
                  @change="loadInvoices"
                  class="w-100"
                >
                  <el-option :label="$t('modern_trade.invoice.status_unpaid')" :value="1"></el-option>
                  <el-option :label="$t('modern_trade.invoice.status_partial')" :value="2"></el-option>
                  <el-option :label="$t('modern_trade.invoice.status_paid')" :value="3"></el-option>
                  <el-option :label="$t('modern_trade.invoice.status_cancelled')" :value="9"></el-option>
                </el-select>
              </div>
              <div class="col-md-2">
                <label class="form-control-label">{{ $t("modern_trade.invoice.date_from") }}</label>
                <el-date-picker
                  v-model="filters.date_from"
                  type="date"
                  value-format="yyyy-MM-dd"
                  clearable
                  class="w-100"
                  @change="loadInvoices"
                ></el-date-picker>
              </div>
              <div class="col-md-2">
                <label class="form-control-label">{{ $t("modern_trade.invoice.date_to") }}</label>
                <el-date-picker
                  v-model="filters.date_to"
                  type="date"
                  value-format="yyyy-MM-dd"
                  clearable
                  class="w-100"
                  @change="loadInvoices"
                ></el-date-picker>
              </div>
              <!-- #165: ปุ่มค้นหาท้ายสุดของแถว filter ความสูงเท่ากับ input อื่น -->
              <div class="col-md-2">
                <el-button
                  type="primary"
                  icon="el-icon-search"
                  class="w-100 mt-invoice-search-btn"
                  @click="loadInvoices"
                >{{ $t("search") }}</el-button>
              </div>
            </div>

            <div v-loading="loading">
              <el-table :data="invoiceRows" row-key="id" header-row-class-name="thead-light">
                <el-table-column :label="$t('modern_trade.invoice.doc_no')" prop="doc_no" min-width="130"></el-table-column>
                <el-table-column :label="$t('modern_trade.invoice.company')" min-width="160">
                  <template slot-scope="{ row }">{{ row.company_name || row.company_id }}</template>
                </el-table-column>
                <el-table-column :label="$t('modern_trade.invoice.invoice_date')" min-width="110">
                  <template slot-scope="{ row }">{{ formatDate(row.invoice_date) }}</template>
                </el-table-column>
                <el-table-column :label="$t('modern_trade.invoice.due_date')" min-width="110">
                  <template slot-scope="{ row }">{{ formatDate(row.due_date) }}</template>
                </el-table-column>
                <el-table-column :label="$t('modern_trade.invoice.source')" min-width="110">
                  <template slot-scope="{ row }">{{ sourceLabel(row.source) }}</template>
                </el-table-column>
                <el-table-column :label="$t('modern_trade.invoice.net_amount')" min-width="120" align="right">
                  <template slot-scope="{ row }">{{ formatMoney(row.net_amount) }}</template>
                </el-table-column>
                <el-table-column :label="$t('modern_trade.invoice.paid_amount')" min-width="120" align="right">
                  <template slot-scope="{ row }">{{ formatMoney(row.paid_amount) }}</template>
                </el-table-column>
                <!-- #156: badge สถานะอ่านง่าย (ตัวใหญ่ขึ้น + สีสื่อความหมาย) -->
                <el-table-column :label="$t('modern_trade.invoice.status')" min-width="140" align="center">
                  <template slot-scope="{ row }">
                    <span
                      class="badge mt-status-badge"
                      :class="statusClass(row.status)"
                      :title="row.status === 9 && row.cancel_reason ? row.cancel_reason : ''"
                    >
                      {{ statusLabel(row.status) }}
                    </span>
                    <div v-if="row.status === 9 && row.cancel_reason" class="small text-muted mt-1">
                      {{ $t("modern_trade.invoice.cancel_reason") }}: {{ row.cancel_reason }}
                    </div>
                  </template>
                </el-table-column>
                <el-table-column :label="$t('actions')" min-width="180" align="center">
                  <template slot-scope="{ row }">
                    <base-button size="sm" type="info" @click.native="handlePdf(row)">
                      <i class="fa fa-file-pdf"></i>
                    </base-button>
                    <!-- #303: ใบกำกับใต้ SO ที่ปิดใบแล้ว = แก้ไม่ได้อีก (BE ตีกลับ 409
                         sale_order_closed ถ้าฝืนกด) — ซ่อนปุ่ม mutation ให้ตรงกัน -->
                    <base-button
                      v-if="isEditModernTrade && row.status !== 9 && !isSoClosedRow(row)"
                      size="sm"
                      type="success"
                      @click.native="openPayment(row)"
                    >
                      <i class="fa fa-money-bill"></i>
                    </base-button>
                    <base-button
                      v-if="isEditModernTrade && row.status !== 9 && !isSoClosedRow(row)"
                      size="sm"
                      type="danger"
                      @click.native="handleCancel(row)"
                    >
                      <i class="fa fa-ban"></i>
                    </base-button>
                    <span v-if="isSoClosedRow(row)" class="badge badge-secondary ml-1" :title="$t('modern_trade.invoice.so_closed_hint')">
                      <i class="fa fa-lock"></i>
                    </span>
                  </template>
                </el-table-column>
              </el-table>
            </div>

            <div class="d-flex align-items-center justify-content-between mt-3">
              <p class="text-muted mb-0">{{ $t("total") }}: {{ invoiceTotal }}</p>
              <base-pagination
                v-model="page"
                :per-page="perPage"
                :total="invoiceTotal"
                @input="loadInvoices"
              ></base-pagination>
            </div>
          </el-tab-pane>

          <!-- spec 2026-08-13 (รอบบ่าย): แท็บ "รอบยอดขายที่ยังไม่ออกใบ" ถูกถอดออก —
               ใบกำกับฝากขายออกตาม "เงินที่รับจริง" ที่หน้า "รับเงินฝากขาย"
               (POST /consignment-invoices) ส่วน POST /invoices {sales_batch_id}
               ฝั่ง BE ตอบ 409 use_consignment_invoices แล้ว -->
        </el-tabs>
      </card>
    </div>

    <!-- Record Payment Dialog -->
    <el-dialog
      :title="$t('modern_trade.payment.record')"
      :visible.sync="showPaymentDialog"
      width="620px"
      @closed="resetPaymentForm"
    >
      <div v-if="paymentInvoice">
        <div class="row mb-3">
          <div class="col-6">
            <small class="text-muted d-block">{{ $t("modern_trade.invoice.doc_no") }}</small>
            <strong>{{ paymentInvoice.doc_no }}</strong>
          </div>
          <div class="col-6 text-right">
            <small class="text-muted d-block">{{ $t("modern_trade.invoice.outstanding") }}</small>
            <strong class="text-danger">{{ formatMoney(outstanding) }}</strong>
          </div>
        </div>
        <!-- #303: race — SO ปิดใบไประหว่าง dialog เปิดค้างอยู่ -->
        <div v-if="isSoClosedRow(paymentInvoice)" class="alert alert-secondary mb-3">
          <i class="fa fa-lock"></i> {{ $t("modern_trade.invoice.so_closed_hint") }}
        </div>
        <div class="row" v-else>
          <div class="col-6">
            <div class="form-group">
              <label class="form-control-label">{{ $t("modern_trade.payment.amount") }} *</label>
              <input v-model.number="paymentForm.amount" class="form-control" type="number" min="0" step="0.01" @focus="$event.target.select()" />
            </div>
          </div>
          <div class="col-6">
            <div class="form-group">
              <label class="form-control-label">{{ $t("modern_trade.payment.paid_date") }} *</label>
              <el-date-picker
                v-model="paymentForm.paid_date"
                type="date"
                value-format="yyyy-MM-dd"
                class="w-100"
              ></el-date-picker>
            </div>
          </div>
          <div class="col-6">
            <div class="form-group">
              <label class="form-control-label">{{ $t("modern_trade.payment.method") }}</label>
              <el-select v-model="paymentForm.method" class="w-100">
                <el-option value="cash" :label="$t('modern_trade.payment.method_cash')"></el-option>
                <el-option value="transfer" :label="$t('modern_trade.payment.method_transfer')"></el-option>
                <el-option value="cheque" :label="$t('modern_trade.payment.method_cheque')"></el-option>
                <el-option value="other" :label="$t('modern_trade.payment.method_other')"></el-option>
              </el-select>
            </div>
          </div>
          <div class="col-6">
            <div class="form-group">
              <label class="form-control-label">{{ $t("modern_trade.payment.note") }}</label>
              <input v-model="paymentForm.note" class="form-control" type="text" />
            </div>
          </div>
          <div class="col-12">
            <div class="form-group">
              <label class="form-control-label">{{ $t("modern_trade.payment.slip") }}</label>
              <div>
                <input
                  ref="slipInput"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  style="display:none"
                  @change="onSlipPicked"
                />
                <el-button size="small" :loading="slipUploading" @click="$refs.slipInput.click()">
                  <i class="fa fa-upload"></i> {{ $t("modern_trade.payment.slip_choose") }}
                </el-button>
                <span v-if="paymentForm.slip_object_key" class="ml-2">
                  <a :href="paymentForm.slip_cdn_url" target="_blank" rel="noopener">
                    <i class="fa fa-paperclip"></i> {{ $t("modern_trade.payment.slip_view") }}
                  </a>
                  <i class="fa fa-times text-danger ml-2" style="cursor:pointer" @click="clearSlip"></i>
                </span>
              </div>
              <small class="text-muted d-block mt-1">{{ $t("modern_trade.payment.slip_hint") }}</small>
            </div>
          </div>
        </div>

        <hr />
        <strong class="small text-muted">{{ $t("modern_trade.payment.history") }}</strong>
        <table class="table table-sm table-bordered mt-2" v-if="paymentHistory.length">
          <thead>
            <tr>
              <th>{{ $t("modern_trade.payment.paid_date") }}</th>
              <th class="text-right">{{ $t("modern_trade.payment.amount") }}</th>
              <th>{{ $t("modern_trade.payment.method") }}</th>
              <th>{{ $t("modern_trade.payment.note") }}</th>
              <th>{{ $t("modern_trade.payment.slip") }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in paymentHistory" :key="p.id">
              <td>{{ formatDate(p.paid_date) }}</td>
              <td class="text-right">{{ formatMoney(p.amount) }}</td>
              <td>{{ p.method || "-" }}</td>
              <td>{{ p.note || "-" }}</td>
              <td>
                <a v-if="p.slip_s3_key" :href="slipUrl(p.slip_s3_key)" target="_blank" rel="noopener">
                  <i class="fa fa-paperclip"></i>
                </a>
                <span v-else>-</span>
              </td>
              <td class="text-right">
                <!-- #303: SO ปิดใบแล้ว = ลบรับเงินไม่ได้อีก -->
                <a
                  v-if="!isSoClosedRow(paymentInvoice)"
                  href="#"
                  class="text-danger"
                  @click.prevent="handleDeletePayment(p.id)"
                >
                  <i class="fa fa-trash"></i>
                </a>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="text-muted small">{{ $t("modern_trade.report_sales.no_data") }}</p>
      </div>
      <span slot="footer">
        <el-button @click="showPaymentDialog = false">{{ $t("close") }}</el-button>
        <el-button
          type="primary"
          :loading="savingPayment"
          :disabled="!canSubmitPayment"
          @click="handleRecordPayment"
        >
          {{ $t("save") }}
        </el-button>
      </span>
    </el-dialog>

    <!-- #165: ดู PDF ใบกำกับภาษีแบบเต็มจอ (reuse shared modal) -->
    <pdf-fullscreen-modal
      :visible.sync="showPdf"
      :title="pdfTitle"
      :fetch-pdf="pdfFetcher"
      :filename="pdfFilename"
    ></pdf-fullscreen-modal>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option, Dialog, Button, Tabs, TabPane, DatePicker } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination } from "@/components";
import PdfFullscreenModal from "@/components/Modern/PdfFullscreenModal";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import { ModernTradeService } from "@/resource";

export default {
  name: "MtInvoices",
  components: {
    RouteBreadCrumb,
    BasePagination,
    PdfFullscreenModal,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [Dialog.name]: Dialog,
    [Button.name]: Button,
    [Tabs.name]: Tabs,
    [TabPane.name]: TabPane,
    [DatePicker.name]: DatePicker,
  },
  computed: {
    ...mapGetters({
      isAccessModernTrade: "Auth/isAccessModernTrade",
      isEditModernTrade: "Auth/isEditModernTrade",
    }),
    outstanding() {
      if (!this.paymentInvoice) return 0;
      const net = Number(this.paymentInvoice.net_amount) || 0;
      const paid = Number(this.paymentInvoice.paid_amount) || 0;
      return Math.max(net - paid, 0);
    },
    canSubmitPayment() {
      if (this.isSoClosedRow(this.paymentInvoice)) return false;
      const amt = Number(this.paymentForm.amount) || 0;
      return amt > 0 && amt <= this.outstanding && !!this.paymentForm.paid_date;
    },
  },
  data() {
    return {
      activeTab: "invoices",
      companies: [],
      // Tab 1 — invoices
      loading: false,
      invoiceRows: [],
      invoiceTotal: 0,
      page: 1,
      perPage: 20,
      filters: {
        company_id: null,
        status: null,
        date_from: null,
        date_to: null,
      },
      // #165 PDF fullscreen modal
      showPdf: false,
      pdfTitle: "",
      pdfFilename: "invoice.pdf",
      pdfFetcher: null,
      // Payment dialog
      showPaymentDialog: false,
      paymentInvoice: null,
      paymentHistory: [],
      paymentForm: {
        amount: null,
        paid_date: null,
        method: "cash",
        note: "",
        slip_object_key: "",
        slip_cdn_url: "",
      },
      slipUploading: false,
      savingPayment: false,
      // round-19.1 deep-link: ค้นเอกสารแล้วเด้งมาไฮไลต์แถวใบกำกับ (?invoice_id=)
      deepLinkNarrowed: false,
    };
  },
  async mounted() {
    this.loadCompanies();
    await this.loadInvoices();
    this.applyInvoiceDeepLink();
  },
  methods: {
    async loadCompanies() {
      try {
        const res = await ModernTradeService.companiesList({ per_page: 200, status: 1 });
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.companies = d.items || [];
      } catch (e) {
        // non-fatal
      }
    },
    async loadInvoices() {
      this.loading = true;
      try {
        const params = { page: this.page, per_page: this.perPage };
        if (this.filters.company_id) params.company_id = this.filters.company_id;
        if (this.filters.status) params.status = this.filters.status;
        if (this.filters.date_from) params.date_from = this.filters.date_from;
        if (this.filters.date_to) params.date_to = this.filters.date_to;
        const res = await ModernTradeService.invoicesList(params);
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.invoiceRows = d.items || [];
        this.invoiceTotal = d.total || 0;
      } catch (err) {
        swal.fire({ title: this.errMsg(err), icon: "error" });
      } finally {
        this.loading = false;
      }
    },
    // round-19.1 deep-link (?invoice_id=N from MtDocSearchBox): highlight the row.
    // If it isn't on the current page, fetch the invoice once to learn its company,
    // narrow the filter to that company (server-supported), reload, then highlight.
    async applyInvoiceDeepLink() {
      const id = Number(this.$route.query.invoice_id);
      if (!id) return;
      if (this.highlightInvoiceRow(id)) return;
      if (this.deepLinkNarrowed) return; // only narrow once — avoid loops
      this.deepLinkNarrowed = true;
      try {
        const res = await ModernTradeService.invoiceGet(id);
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        const inv = d.invoice || d || null;
        if (inv && inv.company_id) {
          this.filters.company_id = inv.company_id;
          this.page = 1;
          await this.loadInvoices();
          this.highlightInvoiceRow(id);
        }
      } catch (e) {
        // best-effort deep link — silently ignore
      }
    },
    // Scroll to + briefly outline the invoice row on the current page.
    // Returns true if the row was found (and highlighted), false otherwise.
    highlightInvoiceRow(id) {
      const idx = this.invoiceRows.findIndex(r => Number(r.id) === Number(id));
      if (idx < 0) return false;
      this.$nextTick(() => {
        const rows = this.$el.querySelectorAll(".el-table__body-wrapper tbody tr.el-table__row");
        const tr = rows[idx];
        if (!tr) return;
        if (tr.scrollIntoView) tr.scrollIntoView({ behavior: "smooth", block: "center" });
        tr.classList.add("mt-focus-row");
        setTimeout(() => tr.classList.remove("mt-focus-row"), 2000);
      });
      return true;
    },
    // #165: open the invoice PDF in the shared fullscreen viewer (view + download/close)
    handlePdf(row) {
      const id = row.id;
      this.pdfTitle = row.doc_no || this.$t("modern_trade.invoice.title");
      this.pdfFilename = `${row.doc_no || "invoice"}.pdf`;
      this.pdfFetcher = () => ModernTradeService.invoicePdf(id);
      this.showPdf = true;
    },
    // round-19 §2b: ยกเลิกได้ทุก source แม้มีการรับเงินแล้ว — BE จะ unlink การรับเงิน
    // กลับเข้า pool. ถามเหตุผลก่อน (optional) แล้วส่งใน body { reason }.
    async handleCancel(row) {
      // round-19.1: ใบกำกับฝากขาย (source 2) ที่ไม่ผูกกับใบสั่งขายใด — เตือนว่ายอดรับเงิน
      // ที่ปลดออกจะไม่กลับเข้ายอดรอออกใบของ SO ใด (ต่างจากใบขายขาดที่ยอดกลับเข้า pool).
      const noSo = row.source === 2 || !row.sale_order_id;
      const warnHtml = noSo
        ? `<div class="text-danger mt-2"><i class="fa fa-exclamation-triangle"></i> ${this.$t("modern_trade.invoice.cancel_no_so_warning")}</div>`
        : "";
      const result = await swal.fire({
        title: this.$t("modern_trade.invoice.cancel_confirm"),
        html: `<div>${this.$t("modern_trade.invoice.cancel_hint")}</div>${warnHtml}`,
        icon: "warning",
        input: "textarea",
        inputPlaceholder: this.$t("modern_trade.invoice.cancel_reason_placeholder"),
        showCancelButton: true,
        confirmButtonText: this.$t("confirm") || "Confirm",
        cancelButtonText: this.$t("close") || "Cancel",
      });
      // sweetalert2 v9: ผู้ใช้กด Confirm → result.value เป็นสตริง (อาจว่างได้)
      if (result.value === undefined) return;
      try {
        await ModernTradeService.invoiceCancelR19(row.id, { reason: (result.value || "").trim() || null });
        swal.fire({ title: this.$t("modern_trade.invoice.cancelled"), icon: "success", timer: 1500, showConfirmButton: false });
        this.loadInvoices();
      } catch (err) {
        this.showErr(err);
      }
    },
    // #303: MtDocFormatConfig ไม่เกี่ยว — นี่คือ SO ที่ invoice ผูกอยู่ ปิดใบแล้วหรือยัง.
    // BE PR #316 (bo-api) กำลังส่ง `sale_order_status` มาใน list/get invoice; ถ้า BE ยังไม่ส่ง
    // (undefined) ให้ทำงานเหมือนเดิมทุกประการ — ไม่พัง ไม่ซ่อนปุ่มผิดที่.
    isSoClosedRow(row) {
      return !!row && row.sale_order_status === 6;
    },
    // จุดเดียวสำหรับโชว์ error ของ mutation ในหน้านี้ — ถ้า BE ตีกลับ 409
    // sale_order_closed (เช่น อีกแท็บปิดใบ SO ระหว่างที่หน้านี้เปิดค้างอยู่) ให้ปิด dialog
    // รับเงิน (ถ้าเปิดอยู่) + โหลด list ใหม่ ปุ่ม mutation ของแถวนั้นจะหายไปเอง
    showErr(err) {
      swal.fire({ title: this.errMsg(err), icon: "error" });
      const status = err && err.response && err.response.status;
      const code = (err && err.response && err.response.data && err.response.data.Msg) || "";
      if (status === 409 && code === "sale_order_closed") {
        this.showPaymentDialog = false;
        this.loadInvoices();
      }
    },
    async openPayment(row) {
      this.paymentInvoice = row;
      this.showPaymentDialog = true;
      try {
        const res = await ModernTradeService.invoiceGet(row.id);
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.paymentInvoice = d.invoice || d || row;
        this.paymentHistory = d.payments || (d.invoice && d.invoice.payments) || [];
      } catch (e) {
        this.paymentHistory = [];
      }
    },
    resetPaymentForm() {
      this.paymentForm = { amount: null, paid_date: null, method: "cash", note: "", slip_object_key: "", slip_cdn_url: "" };
      this.paymentInvoice = null;
      this.paymentHistory = [];
      if (this.$refs.slipInput) this.$refs.slipInput.value = "";
    },
    clearSlip() {
      this.paymentForm.slip_object_key = "";
      this.paymentForm.slip_cdn_url = "";
      if (this.$refs.slipInput) this.$refs.slipInput.value = "";
    },
    slipUrl(key) {
      if (!key) return "";
      if (/^https?:\/\//i.test(key)) return key;
      return "https://cdn.madamefin.com/" + String(key).replace(/^\/+/, "");
    },
    async onSlipPicked(ev) {
      const file = ev.target.files && ev.target.files[0];
      if (!file) return;
      const okType = ["image/jpeg", "image/png", "image/webp", "application/pdf"].indexOf(file.type) !== -1;
      if (!okType) {
        swal.fire({ title: this.$t("modern_trade.payment.slip_bad_type"), icon: "warning" });
        this.clearSlip();
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        swal.fire({ title: this.$t("modern_trade.payment.slip_too_big"), icon: "warning" });
        this.clearSlip();
        return;
      }
      this.slipUploading = true;
      try {
        const presignRes = await ModernTradeService.slipPresign({
          file_name: file.name,
          content_type: file.type,
        });
        const d = (presignRes.data && presignRes.data.Data && presignRes.data.Data[0]) || {};
        if (!d.upload_url || !d.object_key) throw new Error("presign failed");
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("PUT", d.upload_url);
          xhr.setRequestHeader("Content-Type", file.type);
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error("Upload failed: " + xhr.status));
          };
          xhr.onerror = () => reject(new Error("Network error"));
          xhr.send(file);
        });
        this.paymentForm.slip_object_key = d.object_key;
        this.paymentForm.slip_cdn_url = d.cdn_url || this.slipUrl(d.object_key);
      } catch (err) {
        const detail = err && err.message ? ": " + err.message : "";
        swal.fire({ title: this.$t("modern_trade.payment.slip_upload_failed") + detail, icon: "error" });
        this.clearSlip();
      } finally {
        this.slipUploading = false;
      }
    },
    async handleRecordPayment() {
      if (!this.paymentForm.amount || Number(this.paymentForm.amount) <= 0) {
        swal.fire({ title: this.$t("modern_trade.payment.amount_required"), icon: "warning" });
        return;
      }
      if (Number(this.paymentForm.amount) > this.outstanding) {
        swal.fire({ title: this.$t("modern_trade.payment.over_net"), icon: "warning" });
        return;
      }
      if (!this.paymentForm.paid_date) {
        swal.fire({ title: this.$t("modern_trade.payment.date_required"), icon: "warning" });
        return;
      }
      this.savingPayment = true;
      try {
        await ModernTradeService.invoicePayments(this.paymentInvoice.id, {
          amount: this.paymentForm.amount,
          paid_date: this.paymentForm.paid_date,
          method: this.paymentForm.method,
          note: this.paymentForm.note,
          slip_object_key: this.paymentForm.slip_object_key || null,
        });
        swal.fire({ title: this.$t("modern_trade.payment.recorded"), icon: "success", timer: 1500, showConfirmButton: false });
        await this.openPayment(this.paymentInvoice);
        this.loadInvoices();
        this.paymentForm.amount = null;
        this.paymentForm.note = "";
        this.clearSlip();
      } catch (err) {
        this.showErr(err);
      } finally {
        this.savingPayment = false;
      }
    },
    async handleDeletePayment(paymentId) {
      const result = await swal.fire({
        title: this.$t("modern_trade.payment.delete_confirm"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("confirm") || "Confirm",
        cancelButtonText: this.$t("close") || "Cancel",
      });
      if (!result.value) return;
      try {
        await ModernTradeService.paymentDelete(paymentId);
        swal.fire({ title: this.$t("modern_trade.payment.deleted"), icon: "success", timer: 1500, showConfirmButton: false });
        await this.openPayment(this.paymentInvoice);
        this.loadInvoices();
      } catch (err) {
        this.showErr(err);
      }
    },
    statusLabel(status) {
      switch (status) {
        case 1: return this.$t("modern_trade.invoice.status_unpaid");
        case 2: return this.$t("modern_trade.invoice.status_partial");
        case 3: return this.$t("modern_trade.invoice.status_paid");
        case 9: return this.$t("modern_trade.invoice.status_cancelled");
        default: return "-";
      }
    },
    // #156: สีสถานะ — ยังไม่รับเงิน=warning / รับบางส่วน=info / รับครบ=success / ยกเลิก=danger
    // (badge-secondary ของ Argon = #f7fafc เกือบขาว อ่านไม่ออกบนพื้นตาราง จึงไม่ใช้)
    statusClass(status) {
      switch (status) {
        case 1: return "badge-warning";
        case 2: return "badge-info";
        case 3: return "badge-success";
        case 9: return "badge-danger";
        default: return "badge-neutral-muted";
      }
    },
    sourceLabel(source) {
      switch (source) {
        case 1: return this.$t("modern_trade.invoice.source_outright");
        case 2: return this.$t("modern_trade.invoice.source_consignment");
        default: return "-";
      }
    },
    formatDate(v) {
      if (!v) return "-";
      const d = new Date(v);
      if (isNaN(d.getTime())) return v;
      const pad = n => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    },
    formatMoney(v) {
      const n = Number(v) || 0;
      return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    errMsg(err) {
      return (err.response && err.response.data && err.response.data.Cause) || err.message;
    },
  },
};
</script>

<style scoped>
/* #165: search button same height as el-input/el-select (40px default) */
.mt-invoice-search-btn {
  height: 40px;
}

/*
 * #156: badge สถานะใบกำกับภาษี
 * Argon `.badge` ตั้ง font-size: 66% + uppercase -> ~8px บนตาราง อ่านไม่ออก
 * ที่นี่ล็อกขนาดเป็น px จริง ไม่ให้ขึ้นกับ font-size ของ cell
 */
.mt-status-badge {
  display: inline-block;
  min-width: 104px;
  padding: 0.4rem 0.75rem;
  font-size: 0.8125rem; /* 13px */
  font-weight: 600;
  line-height: 1.25;
  text-transform: none;
  white-space: nowrap;
  border-radius: 0.375rem;
}

/* fallback สถานะที่ไม่รู้จัก: เทากลาง (ไม่ใช้ badge-secondary ที่เกือบขาว)
   สีคู่นี้ hard-code ไว้เอง ไม่ได้มาจาก $theme-colors ⇒ การแก้ธีมกลางเอื้อมไม่ถึง
   ขาวบน #8898aa วัดได้ 2.95:1 (ตก AA) — #627489 คือเฉดเดิม (hue 212 / sat 17%)
   ที่ไล่ความสว่างลงจนได้ 4.80:1 */
.mt-status-badge.badge-neutral-muted {
  color: #ffffff;
  background-color: #627489;
}

/* round-19.1 deep-link: ไฮไลต์แถวใบกำกับที่ถูกค้นเจอ ~2 วิ (กรอบ/พื้นเหลือง) */
.mt-invoices-page >>> .el-table__row.mt-focus-row > td {
  background-color: #fff3cd !important;
  box-shadow: inset 0 0 0 9999px rgba(255, 193, 7, 0.12);
  transition: background-color 0.3s ease;
}
</style>
